/**
 * Shared analytics schema and recommended event catalogue.
 *
 * Keep this file as the source of truth for event names and parameter shapes
 * so all frontends emit consistent GA4 events.
 */

import type { Sentiment } from './types';

export interface AnalyticsFieldDefinition {
  /** Stable identifier used in Firebase/GA configuration docs. */
  id: string;
  /** Analytics key name used in payloads. */
  name: string;
  /** Human-readable purpose of the field. */
  description: string;
}

export interface AnalyticsEventDefinition {
  id: string;
  name: AnalyticsEventName;
  description: string;
  parameters: readonly AnalyticsFieldDefinition[];
}

export const analyticsProperties = {
  environment: {
    id: 'analytics_property_environment',
    name: 'environment',
    description: 'App environment for the event source (development, staging, production).',
  },
  appSurface: {
    id: 'analytics_property_app_surface',
    name: 'app_surface',
    description: 'Frontend surface where the event originated (frontend, cms, ar).',
  },
} as const satisfies Record<string, AnalyticsFieldDefinition>;

export const analyticsDimensions = {
  authMethod: {
    id: 'analytics_dimension_auth_method',
    name: 'method',
    description: 'Authentication method used to sign in or sign up.',
  },
  contentType: {
    id: 'analytics_dimension_content_type',
    name: 'content_type',
    description: 'Type of content involved in the interaction.',
  },
  itemListId: {
    id: 'analytics_dimension_item_list_id',
    name: 'item_list_id',
    description: 'Identifier for the list where the content was shown.',
  },
  itemListName: {
    id: 'analytics_dimension_item_list_name',
    name: 'item_list_name',
    description: 'Display name for the list where the content was shown.',
  },
  itemId: {
    id: 'analytics_dimension_item_id',
    name: 'item_id',
    description: 'Stable identifier for a selected content item.',
  },
  itemName: {
    id: 'analytics_dimension_item_name',
    name: 'item_name',
    description: 'Human-readable name for a selected content item.',
  },
  formId: {
    id: 'analytics_dimension_form_id',
    name: 'form_id',
    description: 'Stable identifier for a submitted form.',
  },
  formName: {
    id: 'analytics_dimension_form_name',
    name: 'form_name',
    description: 'Display name for a submitted form.',
  },
  submissionStatus: {
    id: 'analytics_dimension_submission_status',
    name: 'submission_status',
    description: 'Outcome of a form submission attempt.',
  },
  storySentiment: {
    id: 'analytics_dimension_story_sentiment',
    name: 'story_sentiment',
    description: 'Sentiment classification attached to the viewed story.',
  },
  storyThemes: {
    id: 'analytics_dimension_story_themes',
    name: 'story_themes',
    description: 'Pipe-delimited list of AI-detected story themes.',
  },
  storyCoreTags: {
    id: 'analytics_dimension_story_core_tags',
    name: 'story_core_tags',
    description: 'Pipe-delimited list of core tags applied to a story.',
  },
  fromLocation: {
    id: 'analytics_dimension_from_location',
    name: 'from_location',
    description: 'Story origin location, formatted as "city, country".',
  },
  currentLocation: {
    id: 'analytics_dimension_current_location',
    name: 'current_location',
    description: 'Story current location, formatted as "city, country".',
  },
  arEntryPoint: {
    id: 'analytics_dimension_ar_entry_point',
    name: 'ar_entry_point',
    description: 'How a user entered the AR experience.',
  },
  arExitReason: {
    id: 'analytics_dimension_ar_exit_reason',
    name: 'ar_exit_reason',
    description: 'Reason the user left the AR experience.',
  },
  placementMethod: {
    id: 'analytics_dimension_placement_method',
    name: 'placement_method',
    description: 'How AR content placement was triggered.',
  },
} as const satisfies Record<string, AnalyticsFieldDefinition>;

export const analyticsMetrics = {
  itemsCount: {
    id: 'analytics_metric_items_count',
    name: 'items_count',
    description: 'Number of items loaded in the currently viewed list.',
  },
  storyThemeCount: {
    id: 'analytics_metric_story_theme_count',
    name: 'story_theme_count',
    description: 'Number of AI themes detected for the story.',
  },
  storyCoreTagCount: {
    id: 'analytics_metric_story_core_tag_count',
    name: 'story_core_tag_count',
    description: 'Number of core tags assigned to the story.',
  },
  storyStarRating: {
    id: 'analytics_metric_story_star_rating',
    name: 'story_star_rating',
    description: 'Moderator star rating assigned to the story (1-5).',
  },
  storyPrivacyRisk: {
    id: 'analytics_metric_story_privacy_risk',
    name: 'story_privacy_risk',
    description: 'Story privacy risk score from analysis (0-100).',
  },
  storyAudioQuality: {
    id: 'analytics_metric_story_audio_quality',
    name: 'story_audio_quality',
    description: 'Story audio quality score from analysis (0-100).',
  },
} as const satisfies Record<string, AnalyticsFieldDefinition>;

export const AnalyticsEvents = {
  AR_CONTENT_POSITIONED: 'ar_content_positioned',
  AR_EXIT: 'ar_exit',
  AR_LAUNCH: 'ar_launch',
  LOGIN: 'login',
  SELECT_CONTENT: 'select_content',
  SIGN_UP: 'sign_up',
  SUBMIT_FORM: 'submit_form',
  VIEW_ITEM: 'view_item',
  VIEW_ITEM_LIST: 'view_item_list',
} as const;

export type AnalyticsEventName = typeof AnalyticsEvents[keyof typeof AnalyticsEvents];

export type AnalyticsAuthMethod = 'email' | 'github' | 'anonymous';

export interface AnalyticsEventPayloads {
  [AnalyticsEvents.AR_CONTENT_POSITIONED]: {
    app_surface: 'ar';
    placement_method: 'tap_to_place' | 'reposition';
  };
  [AnalyticsEvents.AR_EXIT]: {
    app_surface: 'ar';
    ar_exit_reason: 'close_button' | 'record_story' | 'error_back';
  };
  [AnalyticsEvents.AR_LAUNCH]: {
    app_surface: 'ar';
    ar_entry_point: 'microsite' | 'minimal_landing' | 'app_clip';
  };
  [AnalyticsEvents.LOGIN]: {
    method: AnalyticsAuthMethod;
  };
  [AnalyticsEvents.SELECT_CONTENT]: {
    content_type: string;
    item_id: string;
    item_name?: string;
  };
  [AnalyticsEvents.SIGN_UP]: {
    method: Exclude<AnalyticsAuthMethod, 'anonymous'>;
  };
  [AnalyticsEvents.SUBMIT_FORM]: {
    form_id: string;
    form_name: string;
    content_type: string;
    submission_status: 'success' | 'error';
  };
  [AnalyticsEvents.VIEW_ITEM]: {
    content_type: string;
    item_id: string;
    item_name?: string;
    story_sentiment?: Sentiment;
    story_themes?: string;
    story_core_tags?: string;
    from_location?: string;
    current_location?: string;
    story_theme_count?: number;
    story_core_tag_count?: number;
    story_star_rating?: number;
    story_privacy_risk?: number;
    story_audio_quality?: number;
  };
  [AnalyticsEvents.VIEW_ITEM_LIST]: {
    item_list_id: string;
    item_list_name: string;
    content_type: string;
    items_count?: number;
  };
}

export const storySubmissionEventDefaults: Pick<
  AnalyticsEventPayloads[typeof AnalyticsEvents.SUBMIT_FORM],
  'form_id' | 'form_name' | 'content_type'
> = {
  form_id: 'story_submission',
  form_name: 'Story submission',
  content_type: 'audio_story',
};

export const recommendedAnalyticsEvents = {
  [AnalyticsEvents.AR_CONTENT_POSITIONED]: {
    id: 'analytics_event_ar_content_positioned',
    name: AnalyticsEvents.AR_CONTENT_POSITIONED,
    description: 'Tracks AR content placement and reposition actions.',
    parameters: [analyticsProperties.appSurface, analyticsDimensions.placementMethod],
  },
  [AnalyticsEvents.AR_EXIT]: {
    id: 'analytics_event_ar_exit',
    name: AnalyticsEvents.AR_EXIT,
    description: 'Tracks why users exit the AR experience.',
    parameters: [analyticsProperties.appSurface, analyticsDimensions.arExitReason],
  },
  [AnalyticsEvents.AR_LAUNCH]: {
    id: 'analytics_event_ar_launch',
    name: AnalyticsEvents.AR_LAUNCH,
    description: 'Tracks AR launch attempts by entry point.',
    parameters: [analyticsProperties.appSurface, analyticsDimensions.arEntryPoint],
  },
  [AnalyticsEvents.LOGIN]: {
    id: 'analytics_event_login',
    name: AnalyticsEvents.LOGIN,
    description: 'Tracks successful authentication to the app.',
    parameters: [analyticsDimensions.authMethod],
  },
  [AnalyticsEvents.SELECT_CONTENT]: {
    id: 'analytics_event_select_content',
    name: AnalyticsEvents.SELECT_CONTENT,
    description: 'Tracks when a user selects a piece of content or CTA.',
    parameters: [analyticsDimensions.contentType, analyticsDimensions.itemId, analyticsDimensions.itemName],
  },
  [AnalyticsEvents.SIGN_UP]: {
    id: 'analytics_event_sign_up',
    name: AnalyticsEvents.SIGN_UP,
    description: 'Tracks successful account creation.',
    parameters: [analyticsDimensions.authMethod],
  },
  [AnalyticsEvents.SUBMIT_FORM]: {
    id: 'analytics_event_submit_form',
    name: AnalyticsEvents.SUBMIT_FORM,
    description: 'Tracks form submission attempts and outcomes.',
    parameters: [analyticsDimensions.formId, analyticsDimensions.formName, analyticsDimensions.contentType, analyticsDimensions.submissionStatus],
  },
  [AnalyticsEvents.VIEW_ITEM]: {
    id: 'analytics_event_view_item',
    name: AnalyticsEvents.VIEW_ITEM,
    description: 'Tracks when a specific content item is viewed or played.',
    parameters: [
      analyticsDimensions.contentType,
      analyticsDimensions.itemId,
      analyticsDimensions.itemName,
      analyticsDimensions.storySentiment,
      analyticsDimensions.storyThemes,
      analyticsDimensions.storyCoreTags,
      analyticsDimensions.fromLocation,
      analyticsDimensions.currentLocation,
      analyticsMetrics.storyThemeCount,
      analyticsMetrics.storyCoreTagCount,
      analyticsMetrics.storyStarRating,
      analyticsMetrics.storyPrivacyRisk,
      analyticsMetrics.storyAudioQuality,
    ],
  },
  [AnalyticsEvents.VIEW_ITEM_LIST]: {
    id: 'analytics_event_view_item_list',
    name: AnalyticsEvents.VIEW_ITEM_LIST,
    description: 'Tracks when a list of content is loaded for users.',
    parameters: [analyticsDimensions.itemListId, analyticsDimensions.itemListName, analyticsDimensions.contentType, analyticsMetrics.itemsCount],
  },
} as const satisfies Record<AnalyticsEventName, AnalyticsEventDefinition>;
